/**
 * Moving a shape and dragging its corners — `property-maps-v2.md` §5.3.
 *
 * "Every completed shape, **move**, **vertex edit**, rename and delete is
 * saved immediately." Drawing one is `use-drawing.ts`; changing one you have
 * already got is this, and it is a separate hook for the same reason: the
 * Select tool is a different gesture grammar from a pen, and folding them
 * together would mean one state machine answering two questions.
 *
 * **A corner within 8 px is a corner; anything else inside is the whole
 * shape.** The same eight pixels snapping uses, because they are the same
 * judgement — "that is near enough to be what you meant".
 *
 * **A rectangle and a circle keep their blob.** Dragging a rectangle's corner
 * moves `a` or `b` and the other three corners follow; dragging a circle's rim
 * changes `radiusFt`. Editing their rings point by point would quietly turn a
 * rectangle into a quadrilateral, which is the one thing the blob exists to
 * prevent.
 */
import { useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import {
  circlePolygon, metresBetween, metresToFeet, rectPolygon, type Geometry, type LngLat,
} from '@rowted/shared'
import { SNAP_PX } from '../draw/index.js'
import type { MapFeature } from '../api.js'

export interface Reshaped {
  /** The shape as it is being dragged, for the canvas to paint. */
  draft: Geometry | null
  /** True while a drag is in progress, so the map does not pan under it. */
  busy: boolean
}

/**
 * How close a press has to be to a corner to be grabbing it.
 *
 * `SNAP_PX` is 8 — a 16px target, which is right under a mouse the person
 * can see the tip of and useless under a thumb. §7 asks for 44px on a coarse
 * pointer at any width, so a finger gets a 22px radius: the handle is drawn
 * the same size either way, because what changes is where a press counts,
 * not how big the dot looks.
 */
export const TOUCH_GRAB_PX = 22

export function useReshape({
  map, feature, enabled, touch, onCommit,
}: {
  map: MapLibreMap | null
  feature: MapFeature | null
  enabled: boolean
  /** A finger rather than a pointer — widens the grab, nothing else. */
  touch?: boolean
  onCommit: (patch: { geometry: Geometry; shape?: unknown }) => void
}): Reshaped {
  const [draft, setDraft] = useState<Geometry | null>(null)
  const [busy, setBusy] = useState(false)
  const held = useRef({ feature, onCommit })
  held.current = { feature, onCommit }

  useEffect(() => {
    if (!map || !feature || !enabled) {
      setDraft(null)
      setBusy(false)
      return undefined
    }

    /* What this drag is doing, decided on the way down and not revisited. */
    let grab: { kind: 'vertex'; index: number } | { kind: 'whole'; from: LngLat } | null = null
    let latest: { geometry: Geometry; shape?: unknown } | null = null

    const start = feature.geometry as Geometry
    const blob = feature.shape as Record<string, unknown> | null
    const grabPx = touch ? TOUCH_GRAB_PX : SNAP_PX

    type Pointer = { point: { x: number; y: number }; lngLat: { lng: number; lat: number } }

    const down = (event: Pointer) => {
      const ring = cornersOf(start)
      const near = ring.findIndex((one) => {
        const at = map.project(one)
        return Math.hypot(at.x - event.point.x, at.y - event.point.y) <= grabPx
      })
      if (near >= 0) grab = { kind: 'vertex', index: near }
      else if (inside(start, [event.lngLat.lng, event.lngLat.lat])) {
        grab = { kind: 'whole', from: [event.lngLat.lng, event.lngLat.lat] }
      } else return
      setBusy(true)
      map.dragPan.disable()
    }

    const move = (event: Pointer) => {
      if (!grab) return
      const to: LngLat = [event.lngLat.lng, event.lngLat.lat]
      latest = grab.kind === 'vertex'
        ? dragCorner(feature.kind, start, blob, grab.index, to)
        : translate(start, blob, grab.from, to)
      setDraft(latest.geometry)
    }

    const up = () => {
      if (!grab) return
      grab = null
      setBusy(false)
      map.dragPan.enable()
      if (latest) held.current.onCommit(latest)
      latest = null
      setDraft(null)
    }

    map.on('mousedown', down)
    map.on('mousemove', move)
    map.on('mouseup', up)
    return () => {
      map.off('mousedown', down)
      map.off('mousemove', move)
      map.off('mouseup', up)
      map.dragPan.enable()
      setDraft(null)
      setBusy(false)
    }
  }, [map, feature, enabled])

  return { draft, busy }
}

/**
 * The points a drag may grab.
 *
 * A polygon's ring repeats its first point to close, and offering the repeat
 * as a ninth handle sitting exactly on the first is how a corner becomes
 * impossible to grab.
 */
export function cornersOf(geometry: Geometry): LngLat[] {
  if (geometry.type === 'Point') return [geometry.coordinates]
  if (geometry.type === 'LineString') return geometry.coordinates
  return (geometry.coordinates[0] ?? []).slice(0, -1)
}

/** A rough inside test, good enough to tell "on the shape" from "off it". */
export function inside(geometry: Geometry, at: LngLat): boolean {
  if (geometry.type === 'Point') return metresBetween(geometry.coordinates, at) < 25
  const ring = cornersOf(geometry)
  if (geometry.type === 'LineString') {
    /* A line has no inside, so "near enough to grab" is the question. */
    return ring.some((one) => metresBetween(one, at) < 15)
  }
  let hit = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i]!
    const [xj, yj] = ring[j]!
    if ((yi > at[1]) !== (yj > at[1])
      && at[0] < ((xj - xi) * (at[1] - yi)) / (yj - yi) + xi) hit = !hit
  }
  return hit
}

/** One corner moved, with a rectangle and a circle keeping their shape. */
export function dragCorner(
  kind: string, start: Geometry, blob: Record<string, unknown> | null,
  index: number, to: LngLat,
): { geometry: Geometry; shape?: unknown } {
  if (kind === 'rect' && blob?.kind === 'rect') {
    /* The ring is a, (bx,ay), b, (ax,by): corners 0 and 2 are the blob's own
       two, and 1 and 3 each move one coordinate of each. */
    const a = blob.a as LngLat
    const b = blob.b as LngLat
    const next: Record<number, [LngLat, LngLat]> = {
      0: [to, b],
      1: [[a[0], to[1]], [to[0], b[1]]],
      2: [a, to],
      3: [[to[0], a[1]], [b[0], to[1]]],
    }
    const [nextA, nextB] = next[index] ?? [a, b]
    return { geometry: rectPolygon(nextA, nextB), shape: { kind: 'rect', a: nextA, b: nextB } }
  }

  if (kind === 'circle' && blob?.kind === 'circle') {
    const centre = blob.center as LngLat
    const radiusM = Math.max(metresBetween(centre, to), 0.01)
    return {
      geometry: circlePolygon(centre, radiusM),
      shape: { kind: 'circle', center: centre, radiusFt: metresToFeet(radiusM) },
    }
  }

  const ring = cornersOf(start).map((one, at) => (at === index ? to : one))
  if (start.type === 'LineString') return { geometry: { type: 'LineString', coordinates: ring } }
  return { geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]!]] } }
}

/** The whole shape, and its blob, moved by the same delta. */
export function translate(
  start: Geometry, blob: Record<string, unknown> | null, from: LngLat, to: LngLat,
): { geometry: Geometry; shape?: unknown } {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const shift = ([lng, lat]: LngLat): LngLat => [lng + dx, lat + dy]

  const geometry: Geometry = start.type === 'Point'
    ? { type: 'Point', coordinates: shift(start.coordinates) }
    : start.type === 'LineString'
      ? { type: 'LineString', coordinates: start.coordinates.map(shift) }
      : { type: 'Polygon', coordinates: [start.coordinates[0]!.map(shift)] }

  if (blob?.kind === 'rect') {
    return {
      geometry,
      shape: { kind: 'rect', a: shift(blob.a as LngLat), b: shift(blob.b as LngLat) },
    }
  }
  if (blob?.kind === 'circle') {
    return { geometry, shape: { ...blob, center: shift(blob.center as LngLat) } }
  }
  return { geometry, ...(blob ? { shape: blob } : {}) }
}
